import { Component, EventEmitter, inject, Input, NgZone, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { UserService } from "../../services/user.service";
import { NgxSmartModalService } from "ngx-smart-modal";
import {
  CreatePickupRequestModel,
  PickUpRequestCompletedModel,
  SearchDonateToModel
} from "../../models/pickup-request.model";
import { PickupRequestService } from "../../services/pickup-request.service";
import { combineLatest, debounceTime, distinctUntilChanged, Subject, switchMap, tap } from "rxjs";
import { LoginEndconsumerResponseData } from "../../models/user.model";
import { GooglePlacesService } from "../../services/google-places.service";
import { AddressModel } from "../../models/address.model";
import PlacesService = google.maps.places.PlacesService;
import { isPlatformBrowser } from '@angular/common';
import { CountryRestrictionService } from "../../services/country-restriction.service";
import { STORE_LINKS } from '../../constants/store-links';

@Component({
  selector: 'app-pickup-request-form',
  templateUrl: './pickup-request-form.component.html',
  styleUrl: './pickup-request-form.component.scss'
})
export class PickupRequestFormComponent implements OnInit {
  private _fb: FormBuilder = inject(FormBuilder);
  private _userService: UserService = inject(UserService);
  private _ngxSmartModalService: NgxSmartModalService = inject(NgxSmartModalService);
  private _pickupRequestService: PickupRequestService = inject(PickupRequestService);
  private _googlePlacesService: GooglePlacesService = inject(GooglePlacesService);
  private _ngZone: NgZone = inject(NgZone);
  private _platformId = inject(PLATFORM_ID);
  private _countryRestrictionService: CountryRestrictionService = inject(CountryRestrictionService);

  @Input() set selectedAddressData(address: any) {
    if (address === undefined || !this.pickupForm) {
      return;
    }

    const countryCode = address?.address?.country_code;
    const displayName = address?.display_name || address?.displayName || '';

    if (!this.isCountryAllowed(countryCode)) {
      this.handleUnsupportedCountry(displayName, countryCode);
      return;
    }

    this.selectedAddress = address;
    this.clearCountryRestrictionError();
    this.pickupForm.patchValue({ address: displayName || '' }, { emitEvent: false });
  }
  @Input() modalMode: boolean = false;
  @Input() set donateToData(donateTo: {id?: number, public_id?: number, name: string, type: 'team' | 'player'} | undefined) {
    if (donateTo !== undefined) {
      this.selectedDonateTo = donateTo;
      this.formInitialize();
      this.dateFromSubscription();
    }
  }
  @Output() success = new EventEmitter<void>();
  pickupForm!: FormGroup;
  addressSuggestions: AddressModel[] = [];
  pickupRequestCompleted!: boolean;
  pickupRequestCompletedData!: PickUpRequestCompletedModel | null;
  loading!: boolean;
  loadingDonationTo!: boolean;
  loadingAddress!: boolean;
  loadingDonationToError!: boolean;
  error!: boolean;
  selectedAddress!: any;
  selectedDonateTo!: SearchDonateToModel | null;
  unsupportedCountryName: string = '';

  addressSearch$: Subject<string> = new Subject<string>();
  donateSearch$: Subject<string> = new Subject<string>();
  donateOptions: SearchDonateToModel[] = [];
  userData!: LoginEndconsumerResponseData | '';

  minDate = new Date();
  isTodaySelected = false;
  minTime = new Date();
  readonly storeLinks = STORE_LINKS;

  constructor() {
    if (!this.pickupForm) {
      this.formInitialize();
      this.dateFromSubscription();
    }
  }

  ngOnInit(): void {
    this.addressSearch$
      .pipe(
        tap(() => this.loadingAddress = true),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query => this._googlePlacesService.getPlacePredictions(query))
      )
      .subscribe({
        next: (results: google.maps.places.AutocompletePrediction[]) => {
          this.loadingAddress = false;
          this.addressSuggestions = results.map((prediction): any => ({
            placeId: prediction.place_id,
            displayName: prediction.description
          }));
        },
        error: () => this.loadingAddress = false
      });

    this.donateSearch$
      .pipe(
        tap((): void => {
          this.loadingDonationTo = true;
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query => this._pickupRequestService.searchDonateTo(query))
      )
      .subscribe({
        next: (result: {query: string, results: SearchDonateToModel[]}) => {
          this.loadingDonationTo = false;
          this.donateOptions = result.results;
        },
        error: (): void => {
          this.loadingDonationTo = false;
          this.clearDonateOption();
          this.loadingDonationToError = true;
        }
      });
  }

  formInitialize(): void {
    if (isPlatformBrowser(this._platformId)) {
      const selectedAddressRaw: string | null = localStorage.getItem('selected-address');
      let storedSelectedAddress: any;

      if (selectedAddressRaw) {
        try {
          storedSelectedAddress = JSON.parse(selectedAddressRaw);
        } catch (error) {
          console.error('Failed to parse stored pickup address', error);
          storedSelectedAddress = undefined;
        }
      }

      let initialAddressValue = '';

      if (storedSelectedAddress && this.isCountryAllowed(storedSelectedAddress?.address?.country_code)) {
        this.selectedAddress = storedSelectedAddress;
        initialAddressValue = storedSelectedAddress.display_name;
      } else {
        this.selectedAddress = undefined;
        if (selectedAddressRaw) {
          localStorage.removeItem('selected-address');
        }
        const storedPickupAddress = localStorage.getItem('pickup-address');
        if (storedPickupAddress) {
          localStorage.removeItem('pickup-address');
        }
      }

      this.pickupForm = this._fb.group({
        bags: ['', [Validators.required, Validators.min(0), Validators.max(10)]],
        donateTo: [this.selectedDonateTo ? this.selectedDonateTo.name : ''],
        address: [initialAddressValue, [Validators.required, Validators.minLength(2)]],
        pickupDateFrom: ['', Validators.required],
        pickupTimeFrom: ['', Validators.required],
        pickupDateTo: ['', Validators.required],
        pickupTimeTo: ['', Validators.required],
        additionalNotes: ['']
      },
        { validators: this.timeRangeValidator() }
      );
    } else {
      console.error('Skipping localStorage access on server');
    }
  }

  dateFromSubscription(): void {
    const dateFromControl = this.pickupForm.get('pickupDateFrom');
    const timeFromControl = this.pickupForm.get('pickupTimeFrom');
    const dateToControl = this.pickupForm.get('pickupDateTo');
    const timeToControl = this.pickupForm.get('pickupTimeTo');

    // Auto-set time when date is first selected
    dateFromControl!.valueChanges.subscribe((dateValue: any) => {
      if (dateValue instanceof Date) {
        const today = new Date();
        this.isTodaySelected = dateValue.toDateString() === today.toDateString();

        if (this.isTodaySelected) {
          this.minTime = new Date();
        }

        if (!timeFromControl?.value) {
          // Set current time
          const now = new Date();
          timeFromControl?.setValue(now);
        }
      }
    });

    combineLatest([
      dateFromControl!.valueChanges,
      timeFromControl!.valueChanges
    ]).subscribe(([dateValue, timeValue]: [any, any]) => {
      if (dateValue instanceof Date && timeValue instanceof Date) {
        const from = new Date(dateValue);
        from.setHours(timeValue.getHours(), timeValue.getMinutes(), 0, 0);
        const to = new Date(from.getTime() + 3 * 60 * 60 * 1000); // Add 3 hours

        // Always update "To" values when "From" values change to maintain the 3-hour gap
        dateToControl?.patchValue(to, { emitEvent: false });
        timeToControl?.patchValue(to, { emitEvent: false });
      }

      if (dateValue instanceof Date) {
        const today = new Date();
        this.isTodaySelected = dateValue.toDateString() === today.toDateString();
        if (this.isTodaySelected) {
          this.minTime = new Date();
        }
      }
    });
  }


  private isCountryAllowed(countryCode: string | undefined): boolean {
    return this._countryRestrictionService.isCountryAllowed(countryCode);
  }

  getAllowedCountriesDisplay(): string {
    return this._countryRestrictionService.getAllowedCountriesDisplay();
  }

  private getCountryDisplayName(countryCode: string | undefined): string {
    return this._countryRestrictionService.getCountryDisplayName(countryCode);
  }

  private setCountryRestrictionError(): void {
    const addressControl = this.pickupForm?.get('address');

    if (!addressControl) {
      return;
    }

    const currentErrors = addressControl.errors ?? {};

    addressControl.setErrors({
      ...currentErrors,
      countryNotSupported: true
    });
    addressControl.markAsTouched();
  }

  private clearCountryRestrictionError(): void {
    this.unsupportedCountryName = '';
    const addressControl = this.pickupForm?.get('address');

    if (!addressControl) {
      return;
    }

    const currentErrors: Record<string, any> = { ...(addressControl.errors ?? {}) };

    if (!currentErrors['countryNotSupported']) {
      return;
    }

    delete currentErrors['countryNotSupported'];
    const hasOtherErrors = Object.keys(currentErrors).length > 0;

    addressControl.setErrors(hasOtherErrors ? currentErrors : null);
    addressControl.updateValueAndValidity({ emitEvent: false });
  }

  private handleUnsupportedCountry(displayName: string, countryCode?: string, countryName?: string): void {
    const addressControl = this.pickupForm?.get('address');

    this.selectedAddress = undefined;
    this.unsupportedCountryName = countryName || this.getCountryDisplayName(countryCode) || displayName;

    if (addressControl) {
      addressControl.setValue(displayName, { emitEvent: false });
    }

    this.setCountryRestrictionError();

    if (isPlatformBrowser(this._platformId)) {
      localStorage.removeItem('selected-address');
      localStorage.removeItem('pickup-address');
    }

    this.addressSuggestions = [];
  }

  timeRangeValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const dateFrom = control.get('pickupDateFrom')?.value;
      const timeFrom = control.get('pickupTimeFrom')?.value;
      const dateTo = control.get('pickupDateTo')?.value;
      const timeTo = control.get('pickupTimeTo')?.value;

      if (dateFrom instanceof Date && timeFrom instanceof Date && dateTo instanceof Date && timeTo instanceof Date) {
        const from = new Date(dateFrom);
        from.setHours(timeFrom.getHours(), timeFrom.getMinutes(), 0, 0);

        const to = new Date(dateTo);
        to.setHours(timeTo.getHours(), timeTo.getMinutes(), 0, 0);

        // Check if 'to' date is before or equal to 'from' date
        if (to <= from) {
          return { timeRangeInvalid: true };
        }

        // Check if the duration is less than 3 hours (3 hours = 3 * 60 * 60 * 1000 milliseconds)
        const duration = to.getTime() - from.getTime();
        const threeHoursInMs = 3 * 60 * 60 * 1000;

        if (duration < threeHoursInMs) {
          return { minimumDurationNotMet: true };
        }
      }

      return null;
    };
  }

  incrementBags(): void {
    const currentValue = this.pickupForm.get('bags')?.value;
    if (currentValue < 10) {
      let value: number = currentValue ? parseInt(currentValue) + 1 : 1;
      this.pickupForm.get('bags')?.setValue(value);
    }
  }

  decrementBags(): void {
    const currentValue = this.pickupForm.get('bags')?.value;
    if (currentValue > 0) {
      let value: number | string = currentValue - 1;
      if (value === 0) value = '';
      this.pickupForm.get('bags')?.setValue(value);
    }
  }

  onAddressSearch(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const query: string = input?.value.trim() || '';

    this.selectedAddress = undefined;
    this.clearCountryRestrictionError();

    if (query.length > 1) {
      this.addressSearch$.next(query);
    } else {
      this.addressSuggestions = [];
      this.loadingAddress = false;
    }
  }


  async selectAddress(address: AddressModel): Promise<void> {
    if (!address?.placeId) {
      return;
    }

    try {
      const placesService: PlacesService = new google.maps.places.PlacesService(document.createElement('div'));
      this.loadingAddress = true;

      placesService.getDetails({ placeId: address.placeId }, (place: any, status: google.maps.places.PlacesServiceStatus) => {
        this._ngZone.run(() => {
          this.loadingAddress = false;

          if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
            console.warn('Failed to fetch place details', status);
            return;
          }

          const components: any[] = place.address_components || [];
          const findComponent = (types: string[]): any =>
            components.find((component: any) => {
              const componentTypes: string[] = component?.types || [];
              return types.every((type) => componentTypes.indexOf(type) !== -1);
            });

          const getComponentValue = (component: any, variant: 'long' | 'short'): string => {
            if (!component) {
              return '';
            }

            if (variant === 'short') {
              return component.short_name || component.shortText || '';
            }

            return component.long_name || component.longText || '';
          };

          const countryComponent = findComponent(['country']);
          const postalComponent = findComponent(['postal_code']);
          const cityComponent = findComponent(['locality']) || findComponent(['administrative_area_level_2']);
          const stateComponent = findComponent(['administrative_area_level_1']);

          const countryCode = getComponentValue(countryComponent, 'short');
          const countryName = getComponentValue(countryComponent, 'long');

          if (!this.isCountryAllowed(countryCode)) {
            this.handleUnsupportedCountry(address.displayName, countryCode, countryName);
            return;
          }

          const normalizedCountryCode = countryCode.toUpperCase();
          const location = place.geometry?.location;
          const resolveCoordinate = (coordinate: any, axis: 'lat' | 'lng'): string => {
            if (!coordinate) {
              return '';
            }

            if (typeof coordinate[axis] === 'function') {
              return coordinate[axis]().toString();
            }

            if (typeof coordinate[axis] === 'number') {
              return coordinate[axis].toString();
            }

            return '';
          };

          const lat = resolveCoordinate(location, 'lat');
          const lng = resolveCoordinate(location, 'lng');

          this.selectedAddress = {
            display_name: address.displayName,
            address: {
              postcode: getComponentValue(postalComponent, 'long') || '',
              city: getComponentValue(cityComponent, 'long') || '',
              state: getComponentValue(stateComponent, 'long') || '',
              country_code: normalizedCountryCode,
            },
            lon: lng ? parseFloat(lng).toString() : '',
            lat: lat ? parseFloat(lat).toString() : ''
          };

          this.clearCountryRestrictionError();
          this.pickupForm.patchValue({ address: address.displayName }, { emitEvent: false });
          this.addressSuggestions = [];

          if (isPlatformBrowser(this._platformId)) {
            localStorage.setItem('pickup-address', address.displayName);
            localStorage.setItem('selected-address', JSON.stringify(this.selectedAddress));
          }
        });
      });
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  }


  onDonateSearch(event: Event): void {
    this.loadingDonationTo = true;
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const query: string = input?.value || '';

    if (query.length > 1) {
      this.donateSearch$.next(query);
    } else {
      this.loadingDonationTo = false;
    }
  }

  selectDonateOption(option: SearchDonateToModel): void {
    this.selectedDonateTo = option;

    this.pickupForm.patchValue({
      donateTo: option.name
    });
    this.donateOptions = []; // Clear options after selection
  }

  clearDonateOption(): void {
    this.selectedDonateTo = null;

    this.pickupForm.patchValue({
      donateTo: null
    });
  }

  onSubmit(): void {
    console.log('onSubmit', this.loading);
    if (this.loading) return;

    this.error = false;
    this.loading = true;
    console.log('this._userService.get()', this._userService.get());
    if (!this._userService.get()) {
      // Open above any existing modals so the registration modal is visible on top
      this._ngxSmartModalService.open('endConsumerRegModal', true);
      // this._ngxSmartModalService.setModalData({withLoginBtn: true}, 'endConsumerRegModal', true);
      this.loading = false;
      return;
    }

    if (this.pickupForm.valid) {
      const formValue = this.pickupForm.value;

      if (!this.selectedAddress) {
        this.loading = false;
        this.pickupForm.get('address')?.markAsTouched();
        return;
      }

      const pickupFrom: string = this.combineDateTime(formValue.pickupDateFrom, formValue.pickupTimeFrom);
      const pickupTo: string = this.combineDateTime(formValue.pickupDateTo, formValue.pickupTimeTo);

      const requestData: CreatePickupRequestModel = {
        bags_number: formValue.bags,
        address_line: formValue.address,
        postal_code: this.selectedAddress.address.postcode || '',
        city: this.selectedAddress.address.city || this.selectedAddress.address.town || '',
        state: this.selectedAddress.address.state || '',
        country: this.selectedAddress.address.country_code.toUpperCase(),
        from_date: pickupFrom,
        to_date: pickupTo,
        additional_notes: formValue.additionalNotes,
        lon: parseFloat(this.selectedAddress.lon).toFixed(6),
        lat: parseFloat(this.selectedAddress.lat).toFixed(6)
      }

      if (this.selectedDonateTo) {
        requestData.donate_to = {
          type: this.selectedDonateTo.type
        }

        if (this.selectedDonateTo.id) requestData.donate_to.id = this.selectedDonateTo.id;
        if (this.selectedDonateTo.public_id) requestData.donate_to.public_id = this.selectedDonateTo.public_id;
      };

      this._pickupRequestService.createPickupRequest(requestData).subscribe(result => {
        this.pickupRequestCompleted = true;
        this.pickupRequestCompletedData = result;
        this.loading = false;
        this.userData = this._userService.get();
        this.success.emit();
      }, error => {

        if (error.error && error.error.detail && error.error.detail.includes('User is not active')) {
          this._ngxSmartModalService.getModal('endConsumerRegModal').open(); // TODO
          this._ngxSmartModalService.setModalData({otpVerification: true}, 'endConsumerRegModal', true);
        } else if (error.error?.code === 'NO_TEAM_IN_AREA') {
          this._ngxSmartModalService.setModalData(error.error.city || '', 'noTeamsModal', true);
          // Open above any existing modals so the notice is visible.
          this._ngxSmartModalService.open('noTeamsModal', true);
        } else {
          this.error = true;
        }

        this.loading = false;
      });
    }
  }

  combineDateTime(date: Date, time: Date): string {
    const combined = new Date(date);
    combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return combined.toISOString().slice(0, 16); // format to 'yyyy-MM-ddTHH:mm'
  }

  backToHome(): void {
    this.pickupRequestCompleted = false;
    this.pickupRequestCompletedData = null;
    this._ngxSmartModalService.getModal('pickupRequestModal').close();
  }
}
